<template>
  <div>
    <div class="waveform-wrapper border rounded mb-1">
      <div ref="container" class="waveform-container" />
      <div v-if="!audioUrl" class="text-center text-muted py-3 placeholder-label">
        Выберите трек для прослушивания
      </div>
    </div>

    <b-alert :show="hasError" variant="danger" class="mb-1">
      Не удалось загрузить аудио
    </b-alert>

    <div class="mb-1 d-flex" style="gap: 16px">
      <div>Начало: <strong>{{ formatTime(localStart) }}</strong> сек</div>
      <div>Длина: <strong>{{ formatTime(localDuration) }}</strong> сек</div>
    </div>

    <div class="d-flex" style="gap: 8px">
      <b-button size="sm" variant="primary" :disabled="!isReady" @click="playRegion">
        <feather-icon :icon="isPlaying ? 'PauseIcon' : 'PlayIcon'" /> Фрагмент
      </b-button>
      <b-button size="sm" variant="outline-secondary" :disabled="!isReady" @click="playAll">
        <feather-icon icon="PlayCircleIcon" /> Целиком
      </b-button>
      <b-button size="sm" variant="outline-warning" :disabled="!isReady" @click="resetRegion">
        <feather-icon icon="RotateCcwIcon" /> Сброс
      </b-button>
    </div>
  </div>
</template>

<script>
import { BButton, BAlert } from 'bootstrap-vue'
import {
  ref, watch, onMounted, onBeforeUnmount,
} from '@vue/composition-api'

export default {
  components: { BButton, BAlert },
  props: {
    audioUrl: { type: String, default: null },
    value: {
      type: Object,
      default: () => ({ startSeconds: 55, durationSeconds: 25 }),
    },
    defaultStart: { type: Number, default: 55 },
    defaultDuration: { type: Number, default: 25 },
  },
  setup(props, { emit }) {
    const container = ref(null)
    const wavesurfer = ref(null)
    const regionsPlugin = ref(null)
    const region = ref(null)
    const isReady = ref(false)
    const isPlaying = ref(false)
    const hasError = ref(false)
    const duration = ref(0)
    const localStart = ref(props.value.startSeconds ?? props.defaultStart)
    const localDuration = ref(props.value.durationSeconds || props.defaultDuration)
    let timeupdateOff = null

    const formatTime = v => (Math.round((v || 0) * 10) / 10).toFixed(1)

    const emitChange = () => {
      emit('input', {
        startSeconds: Math.round(localStart.value * 10) / 10,
        durationSeconds: Math.round(localDuration.value * 10) / 10,
      })
    }

    const stopAtRegionEnd = () => {
      if (timeupdateOff) {
        timeupdateOff()
        timeupdateOff = null
      }
    }

    const recreateRegion = () => {
      if (!regionsPlugin.value) return
      regionsPlugin.value.clearRegions()
      const start = Math.max(0, localStart.value)
      const end = Math.min(duration.value || (start + localDuration.value), start + localDuration.value)
      region.value = regionsPlugin.value.addRegion({
        start,
        end,
        color: 'rgba(0, 128, 255, 0.2)',
        drag: true,
        resize: true,
      })
      region.value.on('update-end', () => {
        localStart.value = region.value.start
        localDuration.value = region.value.end - region.value.start
        emitChange()
      })
    }

    const destroy = () => {
      stopAtRegionEnd()
      try { wavesurfer.value?.destroy() } catch (e) {}
      wavesurfer.value = null
      regionsPlugin.value = null
      region.value = null
      isReady.value = false
      isPlaying.value = false
    }

    const init = async () => {
      hasError.value = false
      isReady.value = false
      if (!props.audioUrl) return
      try {
        const WaveSurfer = (await import('wavesurfer.js')).default
        const RegionsPlugin = (await import('wavesurfer.js/dist/plugins/regions.esm.js')).default

        wavesurfer.value = WaveSurfer.create({
          container: container.value,
          waveColor: '#7367f0',
          progressColor: '#5e50ee',
          cursorColor: '#ea5455',
          height: 80,
          responsive: true,
        })
        regionsPlugin.value = wavesurfer.value.registerPlugin(RegionsPlugin.create())

        wavesurfer.value.on('ready', () => {
          duration.value = wavesurfer.value.getDuration()
          // clamp начало внутрь длины аудио
          const safeDuration = Math.min(localDuration.value, duration.value)
          const maxStart = Math.max(0, duration.value - safeDuration)
          if (localStart.value > maxStart) localStart.value = maxStart
          localDuration.value = safeDuration
          isReady.value = true
          recreateRegion()
          emitChange()
        })
        wavesurfer.value.on('error', () => {
          hasError.value = true
        })
        wavesurfer.value.on('finish', () => {
          isPlaying.value = false
          stopAtRegionEnd()
        })
        wavesurfer.value.load(props.audioUrl)
      } catch (e) {
        hasError.value = true
      }
    }

    const playRegion = () => {
      if (!region.value || !wavesurfer.value) return
      if (isPlaying.value) {
        wavesurfer.value.pause()
        isPlaying.value = false
        stopAtRegionEnd()
        return
      }

      const end = region.value.end
      wavesurfer.value.setTime(region.value.start)
      wavesurfer.value.play()
      isPlaying.value = true

      stopAtRegionEnd()
      const off = wavesurfer.value.on('timeupdate', currentTime => {
        if (currentTime >= end) {
          wavesurfer.value.pause()
          isPlaying.value = false
          stopAtRegionEnd()
        }
      })
      timeupdateOff = typeof off === 'function' ? off : () => {}
    }

    const playAll = () => {
      if (!wavesurfer.value) return
      stopAtRegionEnd()
      wavesurfer.value.play()
      isPlaying.value = true
    }

    const resetRegion = () => {
      const safeDuration = Math.min(props.defaultDuration, duration.value || props.defaultDuration)
      const maxStart = Math.max(0, (duration.value || (props.defaultStart + safeDuration)) - safeDuration)
      localStart.value = Math.min(props.defaultStart, maxStart)
      localDuration.value = safeDuration
      recreateRegion()
      emitChange()
    }

    onMounted(() => { init() })

    watch(() => props.audioUrl, () => {
      destroy()
      init()
    })

    onBeforeUnmount(destroy)

    return {
      container, isReady, isPlaying, hasError, duration, localStart, localDuration,
      playRegion, playAll, resetRegion, formatTime,
    }
  },
}
</script>

<style scoped>
.waveform-wrapper {
  position: relative;
  background: #f8f8f8;
  min-height: 96px;
  padding: 8px;
}
.waveform-container {
  min-height: 80px;
  width: 100%;
}
.placeholder-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
</style>
